// ABOUTME: Icon button + modal for the user's persistent "specialized content" (confirmed truths).
// ABOUTME: Self-contained — loads/saves via userApi and is shared by the home input and chat input.

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FileText, Loader2, Upload, X } from 'lucide-react';
import { userApi } from '@/services/api/client';

interface SpecializedContentButtonProps {
  /** Extra classes for the trigger button so it matches its host toolbar. */
  className?: string;
}

/**
 * Button that opens a modal where the user pastes confirmed facts about their
 * questions. The content is persisted per-user and injected into the SIR and
 * known-question prompts on every investigation. A dot indicator shows when
 * content is attached.
 */
export function SpecializedContentButton({ className = '' }: SpecializedContentButtonProps) {
  const [content, setContent] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isParsing, setIsParsing] = useState(false);
  const [parseError, setParseError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;
    userApi
      .getSpecializedContent()
      .then((res) => {
        if (!cancelled) setContent(res.content || '');
      })
      .catch(() => {
        // Silent — the feature is optional and must never block the chat UI.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const open = useCallback(() => {
    setDraft(content);
    setParseError('');
    setIsOpen(true);
  }, [content]);

  const handlePdfSelected = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      // Reset the input so selecting the same file again re-triggers onChange.
      e.target.value = '';
      if (!file) return;

      setParseError('');
      setIsParsing(true);
      try {
        const res = await userApi.parseSpecializedContentPdf(file);
        const parsed = (res.content || '').trim();
        // Append to any existing draft rather than clobbering the user's edits.
        setDraft((prev) => (prev.trim() ? `${prev.trim()}\n\n${parsed}` : parsed));
      } catch {
        setParseError("Couldn't read that PDF. Try another file or paste the text manually.");
      } finally {
        setIsParsing(false);
      }
    },
    []
  );

  const save = useCallback(async () => {
    setIsSaving(true);
    try {
      const res = await userApi.setSpecializedContent(draft);
      setContent(res.content || '');
      setIsOpen(false);
    } catch {
      // Keep the modal open on failure so the user's draft isn't lost.
    } finally {
      setIsSaving(false);
    }
  }, [draft]);

  return (
    <>
      <button
        type="button"
        onClick={open}
        title={
          content
            ? 'Specialized content is attached — click to edit'
            : 'Add specialized content for the agent'
        }
        aria-label="Add specialized content"
        className={`relative text-surface-400 hover:text-surface-800 hover:bg-surface-100 rounded-sm cursor-pointer transition-colors shrink-0 ${className}`}
        data-tour="specialized-content-button"
      >
        <FileText className="w-5 h-5" />
        {content.trim() && (
          <span
            className="absolute top-1 right-1 w-2 h-2 rounded-full bg-primary-500 ring-2 ring-surface-0"
            aria-hidden="true"
          />
        )}
      </button>

      {isOpen && createPortal(
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4"
          onClick={() => setIsOpen(false)}
        >
          <div
            className="w-full max-w-lg bg-surface-0 rounded-md shadow-xl border border-surface-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between px-5 pt-5 pb-3 border-b border-surface-200">
              <div>
                <h3 className="text-base font-semibold text-surface-900">Specialized content</h3>
                <p className="mt-1 text-sm text-surface-500">
                  Paste confirmed facts about your questions — the agent treats these as ground
                  truth and follows them. Saved to your account and applied to every investigation.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="ml-3 p-1 text-surface-400 hover:text-surface-800 cursor-pointer shrink-0"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-5 py-4 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf,.pdf"
                  className="hidden"
                  onChange={handlePdfSelected}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isParsing}
                  className="inline-flex items-center gap-2 rounded-sm border border-surface-200 px-3 py-1.5 text-sm text-surface-700 hover:bg-surface-100 cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isParsing ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Reading PDF…
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      Upload a PDF
                    </>
                  )}
                </button>
                {parseError && (
                  <span className="text-xs text-red-600 text-right">{parseError}</span>
                )}
              </div>
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="e.g. When I say 'revenue' I mean net revenue excluding PCC. 'The desk' always refers to the booking desk, not the order desk."
                rows={8}
                autoFocus
                className="w-full resize-y rounded-sm border border-surface-200 bg-surface-0 p-3 text-sm text-surface-800 placeholder-surface-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div className="flex items-center justify-end gap-2 px-5 pb-5">
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="px-3 py-2 text-sm text-surface-600 hover:text-surface-900 cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={save}
                disabled={isSaving}
                className="px-4 py-2 text-sm bg-primary-500 hover:bg-primary-600 text-white rounded-sm shadow-sm cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
